import { List } from 'lucide-react';
import type { ComponentProps } from 'react';
import { FormSectionCard, FormSectionHeader } from '@/components/molecules';
import { EstimateLineItemsSection } from '@/components/organisms/estimates/EstimateLineItemsSection';
import {
  DocumentCostPanel,
  useDocumentCostPanelSuppressed,
} from '@/components/organisms/sales/DocumentCostPanel';

type LineItemsSectionProps = ComponentProps<typeof EstimateLineItemsSection>;

/**
 * The line-item editor and the internal cost readout beside it.
 *
 * Extracted so the estimate form and the estimate-template editor render the
 * same card rather than each assembling their own: the panel tracks alongside
 * the lines so margin moves as the work is priced, and it sticks on wide screens
 * because the lines get long and it is the number you are watching while you
 * edit them.
 */
export function DocumentLineItemsCard({
  lineItemsProps,
  costPanelProps,
  description = 'Build the estimate from pricebook or custom items.',
}: {
  lineItemsProps: Omit<LineItemsSectionProps, 'open' | 'onToggle' | 'bare'>;
  /** Omit to hide the internal cost/margin readout entirely. */
  costPanelProps?: ComponentProps<typeof DocumentCostPanel>;
  description?: string;
}) {
  const lines = (
    <EstimateLineItemsSection open bare onToggle={() => undefined} {...lineItemsProps} />
  );

  // The 280px track exists to hold the panel, so it is the PANEL that decides
  // whether to reserve it — not the presence of the props, which says only that
  // a caller offered the numbers. A user under PROFITABILITY who is also past
  // the price rows gets no panel, and reserving the column for it would leave
  // them editing lines at two-column width beside an empty gutter. Asking
  // `useDocumentCostPanelSuppressed` means the layout and the panel cannot
  // drift, and no caller has to remember the rule.
  const costPanelSuppressed = useDocumentCostPanelSuppressed(costPanelProps?.showPriceSummary);
  const showCostPanel = Boolean(costPanelProps) && !costPanelSuppressed;

  return (
    <FormSectionCard className="rounded-lg border bg-card p-3 shadow-none sm:p-4">
      <FormSectionHeader
        iconNode={
          <span className="mt-0.5 shrink-0 text-muted-foreground">
            <List className="h-4 w-4" />
          </span>
        }
        title="Line items"
        description={description}
        className="mb-3"
      />
      {showCostPanel ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_280px]">
          {lines}
          <DocumentCostPanel {...costPanelProps!} className="xl:sticky xl:top-4 xl:self-start" />
        </div>
      ) : (
        lines
      )}
    </FormSectionCard>
  );
}
